export interface PatchNote {
  // Doubles as the "seen" key for the one-time overlay — bump it (e.g. to
  // the release date, or date+suffix if that date's already taken) with
  // each new entry. Must sort after every existing version string.
  // Newest entry first in the array; `date` below is cosmetic display text
  // only and doesn't need to match `version` exactly (see the top entry).
  version: string;
  date: string; // display only, e.g. "27.10.2026"
  title: string;
  items: string[];
}

// Full version history, shown two ways: PatchNotesOverlay auto-shows
// whichever entries are newer than the version a device last saw (once,
// until dismissed); the "Versionshistorie" section under Konto shows this
// whole list on demand. Add a new entry here with each release worth
// announcing — that's the whole process, no other code needs to change.
export const PATCH_NOTES: PatchNote[] = [
  {
    version: "2026-11-02-3",
    date: "02.11.2026",
    title: "Wichtige Daten auf der Startseite als Kacheln",
    items: [
      "Angepinnte wichtige Daten erscheinen auf der Startseite jetzt auch als farbige Kacheln statt als Liste — passend zur Kategorie-Farbe, genau wie auf der Wichtige-Daten-Seite selbst.",
    ],
  },
  {
    version: "2026-11-02-2",
    date: "02.11.2026",
    title: "Erledigte Todos eingeklappt",
    items: [
      "Erledigte Todos sind jetzt standardmäßig eingeklappt und lassen sich per Tippen ein- und ausblenden, statt die offenen Todos nach unten zu verdrängen.",
    ],
  },
  {
    version: "2026-11-02-1",
    date: "02.11.2026",
    title: "Navigationsleiste anpassen & frischere Entfernung",
    items: [
      "Unter „Startseite anpassen“ lässt sich jetzt auch die untere Navigationsleiste auswählen — 2 bis 4 Seiten nach Wahl, pro Gerät unabhängig einstellbar.",
      "Die Entfernung zueinander aktualisiert sich jetzt sofort beim Öffnen der App statt erst nach einer festen Wartezeit, und lässt sich durch Antippen jederzeit manuell neu abrufen.",
    ],
  },
  {
    version: "2026-11-01-2",
    date: "01.11.2026",
    title: "Todos im Kalender",
    items: [
      "Ein Todo mit Fälligkeitsdatum lässt sich jetzt optional auch als Termin im Kalender anzeigen — Text und Datum bleiben synchron, egal ob du sie beim Todo oder beim Termin änderst.",
      "Löscht ihr das Todo, verschwindet auch der zugehörige Termin. Löscht ihr nur den Termin, bleibt das Todo erhalten, nur eben nicht mehr im Kalender.",
    ],
  },
  {
    version: "2026-11-01-1",
    date: "01.11.2026",
    title: "Zusammen seit & Entfernung",
    items: [
      "Unter euren Namen auf der Startseite könnt ihr jetzt optional anzeigen lassen, seit wie vielen Tagen ihr zusammen seid (einstellbar unter Konto).",
      "Ebenfalls optional: Standort teilen zeigt nur die Entfernung in km zwischen euch an, nie den genauen Ort — jederzeit pro Gerät an- und ausschaltbar unter Konto.",
    ],
  },
  {
    version: "2026-10-28-3",
    date: "28.10.2026",
    title: "Schwebende Glas-Navigationsleiste",
    items: [
      "Die untere Navigationsleiste schwebt jetzt frei über dem Inhalt — abgerundet, mit Glas-Effekt (unscharfer, durchscheinender Hintergrund), ähnlich wie bei neueren iOS-Apps.",
    ],
  },
  {
    version: "2026-10-28-2",
    date: "28.10.2026",
    title: "Wichtige Daten als Kacheln",
    items: [
      "Wichtige Daten werden jetzt als farbige Kacheln statt als Liste angezeigt — die Farbe kommt von der jeweiligen Kategorie, die ihr selbst frei wählen könnt.",
    ],
  },
  {
    version: "2026-10-28-1",
    date: "28.10.2026",
    title: "Kommentare bearbeiten",
    items: [
      "Eigene Kommentare bei Todos und Kalender-Terminen lassen sich jetzt nachträglich bearbeiten (nur der eigene, nicht der des Partners/der Partnerin) — bearbeitete Kommentare sind mit „(bearbeitet)“ markiert.",
    ],
  },
  {
    version: "2026-10-27-6",
    date: "27.10.2026",
    title: "Glocke zeigt nur noch Partner-Aktivität",
    items: ["Die Neuigkeiten-Glocke zeigt jetzt nur noch, was dein Partner/deine Partnerin gemacht hat — deine eigenen Termine, Todos und Kommentare tauchen dort nicht mehr auf."],
  },
  {
    version: "2026-10-27-5",
    date: "27.10.2026",
    title: "Präzise Links in der Neuigkeiten-Glocke",
    items: [
      "Ein Eintrag in der Glocke führt jetzt genau dorthin, statt nur auf die allgemeine Seite — bei einem Termin direkt zu dessen Datum im Kalender, bei einem Kommentar direkt in den jeweiligen Kommentar-Thread, bei Listen/Link-Sammlungen direkt in die jeweilige Liste.",
      "Im Kalender: Antippen eines Datums springt jetzt direkt zur vollständigen Terminliste darunter (inkl. Kommentar-Button) statt manuell runterscrollen zu müssen.",
    ],
  },
  {
    version: "2026-10-27-4",
    date: "27.10.2026",
    title: "Wiederholung mit Ende",
    items: [
      "Wöchentliche, monatliche und jährliche Termine lassen sich jetzt nach einer bestimmten Anzahl an Terminen automatisch enden lassen, statt für immer zu laufen.",
    ],
  },
  {
    version: "2026-10-27-3",
    date: "27.10.2026",
    title: "Kommentare in der Neuigkeiten-Glocke",
    items: ["Wenn jemand einen Kommentar zu einem Todo oder Termin schreibt, taucht das jetzt auch in der Neuigkeiten-Glocke auf — mit Name und kurzer Vorschau des Kommentars."],
  },
  {
    version: "2026-10-27-2",
    date: "26.10.2026",
    title: "Neuigkeiten-Glocke",
    items: [
      "Neues Glocken-Symbol oben neben dem Menü: klappt wie das Menü von oben herunter und zeigt die 5 neuesten Einträge, was der Partner/die Partnerin zuletzt hinzugefügt hat — Termine, Todos, Listen, wichtige Daten, Notizen und Link-Sammlungen.",
      "Ein roter Punkt markiert, wenn es etwas Neues gibt, das noch nicht angesehen wurde.",
    ],
  },
  {
    // Suffixed "-1" rather than the plain date: an earlier bug in
    // PatchNotesOverlay silently marked this exact date as "seen" for
    // every existing device on their very first load after it shipped —
    // bumping the version string past that stored value is what makes it
    // actually show up for them now that the bug's fixed.
    version: "2026-10-27-1",
    date: "26.10.2026",
    title: "Kommentare & Erwähnungen",
    items: [
      "Todos und Kalender-Termine haben jetzt Kommentar-Threads — für Rückfragen, „erledigt, hab's besorgt“ oder einfach zum Abstimmen.",
      "Mit @Name im Kommentar bekommt dein Partner/deine Partnerin gezielt eine Benachrichtigung — auch wenn Kommentar-Push sonst ausgeschaltet ist.",
      "Neue einzelne Schalter unter Benachrichtigungen: Todo-Kommentare, Termin-Kommentare und Erwähnungen.",
      "Dieser Hinweis hier: einmalig beim nächsten Öffnen, und eine vollständige Versionshistorie unten unter Konto.",
    ],
  },
  {
    version: "2026-10-25",
    date: "25.10.2026",
    title: "Kalender-Abo & mehr Ordnung bei Links",
    items: [
      "Kalender-Abo: eure Termine lassen sich einmalig im eigenen Handy-Kalender abonnieren (nur Wir → Handy, mit Link zum Zurücksetzen).",
      "Links: sortierbar (neueste/älteste, beste Bewertung, alphabetisch) und in eine andere Sammlung verschiebbar.",
      "Lange Kommentare bei Links lassen sich jetzt vollständig lesen, nicht nur abgeschnitten.",
      "Listen: Lebensmittel-Kategorien gibt's jetzt nur noch optional für Einkaufslisten, nicht mehr bei jeder Liste.",
      "Kleine Nachrichten: Schnellvorschläge zum Antippen und ein süßeres Erscheinungsbild beim Öffnen.",
    ],
  },
  {
    version: "2026-10-24",
    date: "24.10.2026",
    title: "Bessere Link-Vorschauen",
    items: [
      "Deutlich mehr Links bekommen jetzt automatisch ein Vorschaubild.",
      "„Vorschau neu laden“, falls bei einem Link trotzdem kein Bild gefunden wurde.",
    ],
  },
  {
    version: "2026-10-23",
    date: "23.10.2026",
    title: "Farbschema, Link-Bewertungen & Fehlerbehebungen",
    items: [
      "Fehler behoben: Push-Benachrichtigungen kamen doppelt für alles.",
      "Neu unter Konto: Farbschema wählbar (mehrere Farboptionen statt nur der Standardfarbe).",
      "Links: Bewertung (1–10) und Kommentare — jede Person unabhängig statt gemeinsam, plus Links auf der Startseite anzeigbar.",
      "Startseite: „Morgen“ jetzt als eigene Gruppe bei den nächsten Terminen, statt alles unter „Demnächst“.",
      "Wichtige Daten: abgelaufene einmalige Termine werden ausgeblendet statt mit „-2 Tage“ angezeigt.",
    ],
  },
  {
    version: "2026-10-22",
    date: "22.10.2026",
    title: "Dark Mode, Push-Benachrichtigungen & neue Funktion „Links“",
    items: [
      "Dark Mode: folgt automatisch der Systemeinstellung des Geräts.",
      "Push-Benachrichtigungen eingeführt, einzeln pro Bereich ein-/ausschaltbar unter Benachrichtigungen.",
      "Wichtige Daten: eigene Kategorien erstellbar, „Zusammen seit…“-Zähler, auswählbar was auf der Startseite erscheint.",
      "Kalender neu gestaltet: durchgehende Balken für mehrtägige Termine, jährliche Wiederholung, „Bis“-Datum zurücksetzbar, Uhrzeit von–bis.",
      "Todos: Fälligkeitsdatum, Farbmarkierung je nach Zuständigkeit, Eingabe direkt ohne Plus-Button.",
      "Listen: Lebensmittel-Kategorien für Einkaufslisten, Eingabe direkt ohne Plus-Button.",
      "Neue Funktion: Links — Sammlungen zum Vergleichen von Rezepten, Hotels & Co. mit automatischer Vorschau.",
      "Anmeldung mit Google hinzugefügt.",
    ],
  },
  {
    version: "2026-10-21",
    date: "21.10.2026",
    title: "Wir ist gestartet",
    items: [
      "Gemeinsamer Kalender, Einkaufsliste, Sparziele, Todos, wichtige Daten, Notizen, Stimmungs-Check-in und kleine Nachrichten zwischen euch beiden.",
      "Todos lassen sich nachträglich bearbeiten, nicht nur abhaken oder löschen.",
    ],
  },
];
